import { HTTPError } from "ky";
import type { z } from "zod";
import { KY_OPTIONS } from "@/config";
import { Auth } from "./auth";
import { apiClient as ky } from "./kyConfig";
import { logApi } from "./logger";
import type { ActionReturnType } from "./types";

type HttpMethod = "get" | "post" | "put" | "delete" | "patch";

type QueryValue = string | number | boolean | Date | null | undefined | Array<string | number>;

type RequestSpec = {
  /** Labels for the dev logger, e.g. ["Schedules", "read"]. */
  log: [endpoint: string, method: string];
  method: HttpMethod;
  /** URL segments after `/api-front/`, e.g. [SchedulesEndpoint.basePath, id, "cancel"]. */
  path: (string | number)[];
  /** Serialized to searchParams; null/undefined/"" entries are dropped, arrays repeat the key, Dates become ISO. */
  query?: Record<string, QueryValue>;
  /** Request body — validate with {@link validateInput} first when a body schema exists. */
  json?: unknown;
  /** Multipart body, for uploads. Not combined with `json`. */
  formData?: FormData;
  /** `text` hands the raw body to `response` — for downloads of plain files rather than JSON. */
  responseAs?: "json" | "text";
  /** The cycles cancel endpoint rejects the trailing slash the URL builder appends. */
  stripTrailingSlash?: boolean;
  /** Skip the Authorization header — for calls made before a token exists (login). */
  skipAuth?: boolean;
  /** Let the caller render API errors instead of using global toasts and redirects. */
  localErrorHandling?: boolean;
  /** Overrides the shared `KY_OPTIONS.timeout`, for the few calls that legitimately take longer. */
  timeout?: number;
};

const toSearchParams = (query: Record<string, QueryValue>) =>
  Object.entries(query).flatMap(([key, value]): [string, string][] => {
    if (value === undefined || value === null || value === "") return [];
    if (Array.isArray(value)) return value.map((item) => [key, String(item)]);
    if (value instanceof Date) return [[key, value.toISOString()]];
    return [[key, String(value)]];
  });

/**
 * Validates request input (body or query params) against its Zod schema, mapping a
 * failure straight to the `ActionReturnType` error envelope so callers can early-return.
 */
export const validateInput = <TSchema extends z.ZodTypeAny>(
  schema: TSchema,
  value: unknown,
): { ok: true; data: z.output<TSchema> } | { ok: false; type: "body_validation"; error: string } => {
  const parsed = schema.safeParse(value);
  return parsed.success
    ? { ok: true, data: parsed.data }
    : { ok: false, type: "body_validation", error: parsed.error.message };
};

/**
 * Executes one API call with the shared envelope every endpoint method used to
 * hand-roll: auth header, ky options, dev logging, response validation, and the
 * `ActionReturnType` result — `request` errors for transport failures,
 * `response_validation` when the payload does not match the schema.
 */
export async function request<TSchema extends z.ZodTypeAny>(
  spec: RequestSpec & { response: TSchema },
): ActionReturnType<z.output<TSchema>>;
export async function request(spec: RequestSpec & { response?: undefined }): ActionReturnType<undefined>;
export async function request(spec: RequestSpec & { response?: z.ZodTypeAny }): Promise<unknown> {
  const auth = Auth.getInstance();
  let url: string = auth.apiUrl(spec.path);
  if (spec.stripTrailingSlash) url = url.replace(/\/$/, "");

  let data: unknown;
  try {
    const response = ky(url, {
      method: spec.method,
      headers: spec.skipAuth ? undefined : auth.getAuthHeader(),
      ...(spec.query ? { searchParams: toSearchParams(spec.query) } : {}),
      ...(spec.json !== undefined ? { json: spec.json } : {}),
      ...(spec.formData ? { body: spec.formData } : {}),
      context: { localErrorHandling: spec.localErrorHandling === true },
      ...KY_OPTIONS,
      ...(spec.timeout !== undefined ? { timeout: spec.timeout } : {}),
    });
    if (!spec.response) data = await response;
    else data = spec.responseAs === "text" ? await response.text() : await response.json();
  } catch (error) {
    const body = error instanceof HTTPError ? await error.response.clone().json().catch(() => undefined) : undefined;
    return {
      ok: false,
      type: "request",
      error: error instanceof Error ? error.message : "An unknown error occurred",
      ...(error instanceof HTTPError ? { status: error.response.status } : {}),
      ...(body === undefined ? {} : { body }),
    };
  }

  if (!spec.response) return { ok: true, data: undefined };

  logApi(spec.log[0], spec.log[1], "response", data);
  const parsed = spec.response.safeParse(data);
  logApi(spec.log[0], spec.log[1], "parsed", parsed);

  if (!parsed.success) {
    return { ok: false, type: "response_validation", error: parsed.error.message };
  }
  return { ok: true, data: parsed.data };
}
